import * as React from 'react';
import { FC } from 'react';

import Alert from 'components/alert/alert';
import AppDownloadSection from 'components/features/components/app-download-section';
import ContentCards from 'components/features/components/braze-content-cards';
import CarouselSection from 'components/features/components/carousel-section';
import FanFavoritesSection from 'components/features/components/fan-favorites';
import { HeroContainer } from 'components/features/components/hero';
import { LockedOffersSection } from 'components/features/components/locked-offers-section';
import { MainHeroContainer } from 'components/features/components/main-hero/main-hero-container';
import { MarketingTileGroup } from 'components/features/components/marketing-tile-group';
import OurServicesSection from 'components/features/components/our-services';
import { RecentItemsSection } from 'components/features/components/recent-items';
import { RewardsSection } from 'components/features/components/rewards-section';
import Trending from 'components/features/components/trending-section';
import { GoogleAdSlot } from 'components/google-ad-slot';
import { OffersSection } from 'components/offers-homepage-tims';
import { IMarketingCardGroupFragment, IMarketingTileGroup } from 'generated/sanity-graphql';
import useMediaQuery from 'hooks/use-media-query';
import { RollUpHomepageBanner } from 'pages/campaigns/roll-up-to-win/homepage-banner';
import ScanForRewards from 'pages/home/scan-for-rewards';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { TimsRewardsPointsSection } from './components/tims-rewards-points-section/tims-rewards-points-section';
import { BRAND_ALLOWED_OFFERS_ON_HOMEPAGE } from './home-page/constants';
import { Section } from './home-page/styles';
import { Components } from './home-page/types';

export const Features: FC<{ components: Components; location: string }> = ({
  components,
  location,
}) => {
  const enableOffers = useFlag(LaunchDarklyFlag.ENABLE_OFFERS);
  const thMediaServicesEnabled = useFlag(LaunchDarklyFlag.ENABLE_TH_MEDIA_SERVICES);
  const isMobile = useMediaQuery('mobile');

  if (!components) {
    return null;
  }
  return (
    <>
      {components.map((item, idx) => {
        if (!item) {
          return null;
        }
        const first = idx === 0;
        const last = idx + 1 === (components?.length || 0);
        switch (item.__typename) {
          case 'MainHero': {
            return <MainHeroContainer key={`${location}_MainHero_${item._id}`} item={item} />;
          }

          case 'Hero': {
            return (
              <div key={`${location}_Hero_${item._id}`}>
                <HeroContainer hero={item} />
              </div>
            );
          }

          case 'BrazeContentCards': {
            return (
              <div key={`${location}_BrazeContentCards_${item._id}`}>
                <ContentCards item={item} />
              </div>
            );
          }

          case 'Alert': {
            return (
              <div key={`${location}_Alert_${item._id}`}>
                <Alert
                  headline={item.heading?.locale || ''}
                  body={item.body?.locale || ''}
                  learnMore={item.learnMoreText?.locale || ''}
                  learnMoreUrl={item.learnMoreUrl?.locale || ''}
                />
              </div>
            );
          }

          case 'MarketingTileCarousel': {
            return (
              <div key={`${location}_MarketingTileCarousel_${item._id}`}>
                <CarouselSection item={item} />
              </div>
            );
          }

          case 'MarketingCardGroup':
            return (
              <Section
                key={`${location}_MarketingCardGroup_${item._id}`}
                first={false}
                last={last}
                shouldRenderDefaultBackground={true}
              >
                <MarketingTileGroup item={(item as unknown) as IMarketingCardGroupFragment} />
              </Section>
            );

          case 'MarketingTileGroup':
            return (
              <Section key={`${location}_MarketingTileGroup_${item._id}`} first={first} last={last}>
                <MarketingTileGroup item={item as IMarketingTileGroup} />
              </Section>
            );

          case 'OffersSection': {
            return (
              enableOffers &&
              BRAND_ALLOWED_OFFERS_ON_HOMEPAGE && (
                <div key={`${location}_OffersSection_${item._id}`}>
                  <OffersSection />
                </div>
              )
            );
          }

          case 'Trending': {
            return (
              <div key={`${location}_Trending_${item._id}`}>
                <Trending item={item} />
              </div>
            );
          }

          case 'FanFavorites': {
            return (
              <div key={`${location}_FanFavorites_${item._id}`}>
                <FanFavoritesSection item={item} />
              </div>
            );
          }

          case 'OurServices': {
            return (
              <div key={`${location}_OurServices_${item._id}`}>
                <OurServicesSection item={item} />
              </div>
            );
          }

          case 'AppDownload': {
            return (
              <div key={`${location}_AppDownload_${item._id}`}>
                <AppDownloadSection item={item?.details} />
              </div>
            );
          }

          case 'ScanForRewards': {
            return (
              isMobile && (
                <div key={`${location}_ScanForRewards_${item._id}`}>
                  <ScanForRewards />
                </div>
              )
            );
          }

          case 'RollUpTheRimSection': {
            return (
              <Section key={`${location}_RUTWMarketingTile_${item._id}`} first={first} last={last}>
                <RollUpHomepageBanner />
              </Section>
            );
          }

          case 'RecentItems': {
            return (
              <div key={`${location}_RecentItems_${item._id}`}>
                <RecentItemsSection />
              </div>
            );
          }

          case 'LockedOffers': {
            return (
              <div key={`${location}_LockedOffers_${item._id}`}>
                <LockedOffersSection />
              </div>
            );
          }

          case 'RewardsSection': {
            return (
              <div key={`${location}_RewardsSection_${item._id}`}>
                <RewardsSection item={item} />
              </div>
            );
          }

          case 'GoogleAdSlot': {
            return thMediaServicesEnabled ? (
              <div key={`${location}_GoogleAdSlot_${item._id}`}>
                <GoogleAdSlot adUnit={item?.adUnitId ?? ''} sizes={item?.adSlotSize ?? ''} />
              </div>
            ) : null;
          }

          case 'TimsRewardsPointsSection': {
            return (
              <TimsRewardsPointsSection
                key={`${location}_TimsRewardsPointsSection_${item._id}`}
                welcomeText={item.welcomeText?.locale ?? ''}
              />
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
};
